import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Plus, SquarePen } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { CompanyFormDialog } from './CompanyFormDialog';
import { useActivateCompany, useCompaniesListWithMeta, useDeactivateCompany } from './useCompanies';
import type { Company } from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const CompaniesPage = () => {
  const { t } = useTranslation('settings');
  const user = useCurrentUser();
  const canManage = hasPermission(user, 'company.manage');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<Company | null>(null);
  const query = useCompaniesListWithMeta({
    q: search || undefined,
    page,
    per_page: perPage,
  });
  const deactivate = useDeactivateCompany();
  const activate = useActivateCompany();
  const rows = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  const columns = useMemo<MasterTableColumn<Company>[]>(
    () => [
      {
        key: 'display_name',
        header: t('efs.settings.companies.fields.display_name'),
        cell: (row) => <span className="font-medium">{row.display_name}</span>,
      },
      {
        key: 'legal_name',
        header: t('efs.settings.companies.fields.legal_name'),
        cell: (row) => row.legal_name,
      },
      {
        key: 'party_type',
        header: t('efs.settings.companies.fields.party_type'),
        cell: (row) => t(`efs.settings.companies.types.${row.party_type}`),
      },
      {
        key: 'registration_info',
        header: t('efs.settings.companies.fields.registration_info'),
        cell: (row) => row.registration_info || '—',
      },
      {
        key: 'short_code',
        header: t('efs.settings.companies.fields.short_code'),
        cell: (row) =>
          row.short_code ? (
            <span className="font-mono text-xs text-efs-text-muted">{row.short_code}</span>
          ) : (
            '—'
          ),
      },
      {
        key: 'active',
        header: t('efs.settings.companies.fields.active'),
        cell: (row) =>
          row.active_flag ? (
            <Badge variant="default">{t('efs.common.status.approved', { ns: 'common' })}</Badge>
          ) : (
            <Badge variant="outline">{t('efs.common.status.retired', { ns: 'common' })}</Badge>
          ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) =>
          canManage ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t('efs.common.actions.menu', { ns: 'common', defaultValue: 'Menu' })}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setEditRow(row)}>
                  <SquarePen className="mr-2 h-4 w-4" />
                  {t('efs.settings.companies.actions.edit')}
                </DropdownMenuItem>
                {row.active_flag ? (
                  <DropdownMenuItem
                    disabled={deactivate.isPending}
                    onSelect={() => deactivate.mutate(row.company_id)}
                  >
                    {t('efs.settings.companies.actions.deactivate')}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    disabled={activate.isPending}
                    onSelect={() => activate.mutate(row.company_id)}
                  >
                    {t('efs.settings.companies.actions.activate')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null,
      },
    ],
    [activate, canManage, deactivate, t],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.companies.title')}
        subtitle={t('efs.settings.companies.subtitle')}
        count={total}
        actions={
          canManage ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('efs.settings.companies.actions.create')}
            </Button>
          ) : null
        }
      />

      <TableToolbar
        count={t('efs.common.messages.count', {
          ns: 'common',
          defaultValue: '{{count}} 件',
          count: total,
        })}
        filters={
          <Input
            value={search}
            onChange={(event) => {
              setPage(1);
              setSearch(event.target.value);
            }}
            placeholder={t('efs.settings.companies.search_placeholder')}
            className="h-8 w-64"
          />
        }
      />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.company_id}
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageChange: setPage,
          onPerPageChange: (nextPerPage) => {
            setPage(1);
            setPerPage(nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE);
          },
        }}
      />

      <CompanyFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      {editRow ? (
        <CompanyFormDialog
          open={!!editRow}
          onOpenChange={(open) => {
            if (!open) setEditRow(null);
          }}
          row={editRow}
        />
      ) : null}
    </div>
  );
};

export default CompaniesPage;
